import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Share2 } from 'lucide-react';
import { getAuthHeaders } from './intra/workoutHelpers';
import { MuscleShareCard, useMuscleShare } from './MuscleShareCard';
import { MUSCLE_COLORS, MuscleBodies, MuscleLegend } from './MuscleBodies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  es: {
    loading: 'Calculando músculos…', empty: 'Sin series registradas para mostrar el mapa muscular.',
    front: 'Frente', back: 'Espalda', none: 'No trabajado', levels: ['Ligero', 'Medio', 'Alto', 'Foco'],
    tap: 'Toca un músculo para ver los ejercicios que lo trabajaron', primary: 'principal', synergist: 'secundario',
    stabilizer: 'estabilizador',
    sets: 'series', unmapped: 'Sin mapa muscular', share: 'Compartir', sharing: 'Generando…',
  },
  en: {
    loading: 'Calculating muscles…', empty: 'No logged sets to build the muscle map.',
    front: 'Front', back: 'Back', none: 'Not trained', levels: ['Light', 'Medium', 'High', 'Focus'],
    tap: 'Tap a muscle to see the exercises that trained it', primary: 'primary', synergist: 'synergist',
    stabilizer: 'stabilizer',
    sets: 'sets', unmapped: 'No muscle map', share: 'Share', sharing: 'Generating…',
  },
};

export const MuscleMap = ({ exercises, clientId, days, language = 'es', className = '', shareable = false, shareTitle = '', shareDate = '', onData }) => {
  const t = L[language] || L.es;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { shareRef, sharing, share } = useMuscleShare(shareTitle || 'muscle-map');

  const payload = useMemo(() => (exercises || []).map((ex) => ({
    exercise_id: ex.exercise_id, exercise_name: ex.exercise_name, exercise_name_es: ex.exercise_name_es, actual_sets: ex.actual_sets || [],
  })), [exercises]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const req = days
      ? axios.get(`${API}/workouts/muscle-heatmap`, { params: { client_id: clientId, days, language }, headers: getAuthHeaders() })
      : axios.post(`${API}/workouts/muscle-summary`, { exercises: payload, client_id: clientId, language }, { headers: getAuthHeaders() });
    req.then((r) => { if (alive) { setData(r.data); onData?.(r.data); } })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [payload, clientId, days, language]);

  const selectedRegion = data?.regions?.find((r) => r.slug === selected) || null;
  const toggle = (slug) => setSelected((s) => (s === slug ? null : slug));

  if (loading) return <div className={`text-sm text-gray-400 py-6 text-center ${className}`} data-testid="muscle-map-loading">{t.loading}</div>;
  if (!data || data.regions.length === 0) {
    const msg = data?.unmapped?.length ? `${t.unmapped}: ${data.unmapped.join(', ')}` : t.empty;
    return <div className={`text-sm text-gray-400 py-6 text-center ${className}`} data-testid="muscle-map-empty">{msg}</div>;
  }

  return (
    <div className={className} data-testid="muscle-map">
      <MuscleBodies data={data} onPress={toggle} labels={t} />
      <MuscleLegend t={t} />

      <div className="mt-4 space-y-1.5" data-testid="muscle-map-bars">
        {data.regions.map((r) => (
          <button
            key={r.slug}
            type="button"
            onClick={() => toggle(r.slug)}
            data-testid={`muscle-bar-${r.slug}`}
            className={`w-full flex items-center gap-2 text-left text-xs rounded-md px-2 py-1 transition-colors ${selected === r.slug ? 'bg-white/10' : 'hover:bg-white/5'}`}
          >
            <span className="w-28 sm:w-36 truncate text-gray-200">{r.label}</span>
            <span className="flex-1 h-2 rounded bg-gray-700 overflow-hidden">
              <span className="block h-full rounded" style={{ width: `${(r.score / data.max_score) * 100}%`, background: MUSCLE_COLORS[r.level - 1] }} />
            </span>
            <span className="w-8 text-right tabular-nums text-gray-400">{r.score}</span>
          </button>
        ))}
      </div>

      {selectedRegion ? (
        <div className="mt-3 rounded-lg bg-black/30 border border-white/10 p-3" data-testid="muscle-map-detail">
          <div className="text-xs font-semibold text-white mb-2">{selectedRegion.label}</div>
          <div className="flex flex-wrap gap-1.5">
            {selectedRegion.exercises.map((e, i) => (
              <span
                key={i}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${e.role === 'primary' ? 'bg-red-600/25 border-red-500/40 text-red-100' : 'bg-gray-600/40 border-gray-500/40 text-gray-200'}`}
              >
                {e.name} · {e.sets} {t.sets} · {t[e.role]}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-[11px] text-gray-500 text-center">{t.tap}</p>
      )}

      {data.unmapped?.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-500 text-center" data-testid="muscle-map-unmapped">{t.unmapped}: {data.unmapped.join(', ')}</p>
      )}

      {shareable && (
        <>
          <button
            type="button"
            onClick={share}
            disabled={sharing}
            data-testid="muscle-map-share-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white transition-colors"
          >
            <Share2 className="w-4 h-4" /> {sharing ? t.sharing : t.share}
          </button>
          <MuscleShareCard ref={shareRef} data={data} title={shareTitle} date={shareDate} language={language} />
        </>
      )}
    </div>
  );
};

export default MuscleMap;
